import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import LandingHero from "../components/LandingHero";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tokenState, setTokenState] = useState("checking"); // checking | valid | invalid | expired
  const [tokenEmail, setTokenEmail] = useState("");
  const [done, setDone] = useState(false);
  const { settings } = useSettings();
  const navigate = useNavigate();
  const brand = settings?.primary_color || "#0D9488";

  useEffect(() => {
    if (!token) {
      setTokenState("invalid");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-password/verify`, { params: { token } });
        if (data?.valid) {
          setTokenState("valid");
          setTokenEmail(data.email || "");
        } else {
          setTokenState(data?.reason === "expired" ? "expired" : "invalid");
        }
      } catch {
        setTokenState("invalid");
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => navigate("/login"), 1600);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (tokenState === "checking") {
      return (
        <div className="mt-10 text-sm text-slate-500" data-testid="reset-password-checking">
          Verifying reset link…
        </div>
      );
    }
    if (tokenState === "invalid" || tokenState === "expired") {
      return (
        <div className="mt-10 space-y-4" data-testid={`reset-password-${tokenState}`}>
          <h2 className="text-xl text-slate-900 uppercase tracking-widest">
            Link {tokenState === "expired" ? "expired" : "invalid"}
          </h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            {tokenState === "expired"
              ? "This password reset link has expired. Reset links are valid for 1 hour."
              : "This password reset link is invalid or has already been used."}
          </p>
          <Link
            to="/forgot-password"
            className="inline-block w-full h-12 leading-[3rem] text-center text-white uppercase tracking-widest font-semibold"
            style={{ backgroundColor: brand }}
            data-testid="reset-password-request-new"
          >
            Request a new link
          </Link>
          <div className="pt-4 border-t border-slate-200">
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        </div>
      );
    }
    if (done) {
      return (
        <div className="mt-10 space-y-4" data-testid="reset-password-success">
          <div className="flex items-center justify-center w-14 h-14 border" style={{ borderColor: brand }}>
            <CheckCircle2 className="w-6 h-6" style={{ color: brand }} />
          </div>
          <h2 className="text-xl text-slate-900 uppercase tracking-widest">Password updated</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            Redirecting you to sign in…
          </p>
        </div>
      );
    }
    return (
      <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="reset-password-form">
        <div className="space-y-2">
          <h2 className="text-xl text-slate-900 uppercase tracking-widest">Choose a new password</h2>
          {tokenEmail && (
            <p className="text-sm text-slate-500 leading-relaxed">
              For account <span className="text-slate-900 font-mono-num">{tokenEmail}</span>
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono-num">
            New password
          </Label>
          <Input
            id="password"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-slate-50 border-slate-200 text-slate-900 h-12 rounded-none focus-visible:ring-[#0D9488] focus-visible:border-[#0D9488] dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
            placeholder="At least 8 characters"
            data-testid="reset-password-new"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm" className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono-num">
            Confirm password
          </Label>
          <Input
            id="confirm"
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="bg-slate-50 border-slate-200 text-slate-900 h-12 rounded-none focus-visible:ring-[#0D9488] focus-visible:border-[#0D9488] dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
            placeholder="Repeat new password"
            data-testid="reset-password-confirm"
          />
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-12 text-white uppercase tracking-widest font-semibold rounded-none transition-colors"
          style={{ backgroundColor: brand }}
          data-testid="reset-password-submit"
        >
          {submitting ? "Updating…" : "Update password"}
        </Button>
        <div className="pt-4 border-t border-slate-200">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      </form>
    );
  };

  return <LandingHero settings={settings}>{renderBody()}</LandingHero>;
}
